/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * My Feedback Page - View user's own feedback history
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, Calendar, Eye, Image as ImageIcon, Filter } from 'lucide-react'
import feedbackService from '../services/feedbackService'
import useFeedbackStore from '../stores/feedbackStore'
import FeedbackCategoryBadge from '../components/feedback/FeedbackCategoryBadge'
import FeedbackStatusBadge from '../components/feedback/FeedbackStatusBadge'
import FeedbackRatingDisplay from '../components/feedback/FeedbackRatingDisplay'
import LoadingSpinner from '../components/LoadingSpinner'
import useStatusFilter from '../hooks/useStatusFilter'
import { useFillViewportHeight } from '../hooks/useFillViewportHeight'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('MyFeedback')

const MyFeedback = () => {
  const {
    feedbackList,
    loading,
    currentPage,
    totalPages,
    totalCount,
    filters,
    setFeedbackList,
    setLoading,
    setFilters,
    setPage
  } = useFeedbackStore()

  const navigate = useNavigate()
  const [showFilters, setShowFilters] = useState(false)
  const [localFilters, setLocalFilters] = useState(filters)

  const { STATUS_OPTIONS, handleStatusToggle, isStatusActive, isShowingAll, hasActiveFilters } =
    useStatusFilter(filters, setFilters)

  // Fill the viewport so the feedback list scrolls internally (header/filters stay pinned above,
  // pagination pinned below) instead of leaving an empty band under a short list.
  const listFillRef = useRef(null)
  const listFillHeight = useFillViewportHeight(listFillRef, {
    enabled: feedbackList.length > 0
  })

  useEffect(() => {
    loadFeedback()
  }, [currentPage, filters])

  const loadFeedback = async () => {
    setLoading(true)

    try {
      const params = {
        page: currentPage,
        limit: 10,
        ...filters
      }

      // Serialize status array as comma-separated string for the API
      if (Array.isArray(params.status)) {
        params.status = params.status.join(',')
      }

      // Remove null/undefined values
      Object.keys(params).forEach((key) => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key]
        }
      })

      const data = await feedbackService.getMyFeedback(params)
      setFeedbackList(data)
    } catch (error) {
      logger.error('Error loading feedback:', error)
      const errorMessage = error.response?.data?.error || 'Failed to load feedback'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleApplyFilters = () => {
    setFilters(localFilters)
    setPage(1) // Reset to first page
    setShowFilters(false)
  }

  const handleClearFilters = () => {
    const clearedFilters = {
      status: ['pending', 'in_review'],
      category: null,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    }
    setLocalFilters(clearedFilters)
    setFilters(clearedFilters)
    setPage(1)
    setShowFilters(false)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading && feedbackList.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <MessageSquare className="w-7 h-7" />
              My Feedback
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              View and track your submitted feedback
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors
                ${
                  hasActiveFilters
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }
              `}
            >
              <Filter className="w-5 h-5" />
              More Filters
              {hasActiveFilters && <span className="w-2 h-2 bg-primary-500 rounded-full" />}
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">{totalCount} total</span>
          </div>
        </div>

        {/* Status Toggle Buttons - always visible */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-1">Status:</span>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleStatusToggle(opt.value)}
              aria-pressed={isStatusActive(opt.value)}
              className={`
                px-3 py-1.5 text-sm font-medium rounded-full border-2 transition-all
                ${
                  isStatusActive(opt.value)
                    ? opt.value === 'pending'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                      : opt.value === 'in_review'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : opt.value === 'resolved'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'border-gray-500 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500'
                }
              `}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => setFilters({ status: null })}
            aria-pressed={isShowingAll}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-full border-2 transition-all
              ${
                isShowingAll
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500'
              }
            `}
          >
            All
          </button>
        </div>

        {/* Additional Filters Panel (category, sort) */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={localFilters.category || ''}
                  onChange={(e) =>
                    setLocalFilters({ ...localFilters, category: e.target.value || null })
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">All Categories</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="content_error">Content Error</option>
                  <option value="ui_ux">UI/UX</option>
                  <option value="performance">Performance</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={`${localFilters.sortBy}-${localFilters.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-')
                    setLocalFilters({ ...localFilters, sortBy, sortOrder })
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="createdAt-desc">Newest First</option>
                  <option value="createdAt-asc">Oldest First</option>
                  <option value="updatedAt-desc">Recently Updated</option>
                  <option value="category-asc">Category (A-Z)</option>
                  <option value="status-asc">Status (A-Z)</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Feedback List */}
      {feedbackList.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No feedback yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {hasActiveFilters
              ? 'No feedback matches your filters. Try adjusting your filters.'
              : "You haven't submitted any feedback yet."}
          </p>
        </div>
      ) : (
        // Filled to the viewport so the list scrolls internally while pagination stays pinned below.
        <div
          ref={listFillRef}
          className="flex flex-col gap-4"
          style={listFillHeight ? { height: listFillHeight } : undefined}
        >
          <div className="space-y-4 flex-1 overflow-auto min-h-0">
            {feedbackList.map((feedback) => (
              <div
                key={feedback.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-l-primary-500 dark:border-l-primary-400"
              >
                <div className="p-6">
                  <div className="flex flex-col space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <FeedbackCategoryBadge category={feedback.category} size="sm" />
                          <FeedbackStatusBadge status={feedback.status} size="sm" />
                          {feedback.screenshot && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              <ImageIcon className="w-3 h-3" />
                              Screenshot
                            </span>
                          )}
                        </div>
                        {feedback.rating && (
                          <FeedbackRatingDisplay rating={feedback.rating} size="sm" />
                        )}
                      </div>
                      <button
                        onClick={() => navigate(`/my-feedback/${feedback.id}`)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                    </div>

                    {/* Message Preview */}
                    <div>
                      <p className="text-gray-900 dark:text-gray-100 line-clamp-2">
                        {feedback.message}
                      </p>
                    </div>

                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {formatDate(feedback.createdAt)}
                      </div>
                      {feedback.adminResponse && (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-medium">
                          Admin Responded
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination — pinned below the scrolling list inside the fill region. */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 shrink-0">
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MyFeedback
